import {
  FEATURE_FLAGS_IDS,
  UsageEventTypes,
  portalBuildFocusTarget,
} from '@cc/schema';
import * as Sentry from '@sentry/react';
import { formatISO } from 'date-fns';
import { gqlDataProvider } from '../App';

/**
 * E03 · the PORTAL's usage emits (M7, 2026-09-13).
 *
 * ─── WHY THE PORTAL HAD NONE ───────────────────────────────────────────────────────────────────
 * `LoggedIn` (12) has been emitted by the mobile app since the beginning and by the portal never,
 * so every "who is using CopingCard" answer has silently meant "on a phone". A therapist who signs
 * in to the portal every working day has been invisible to the same counters that watch their
 * clients. M7 closes that.
 *
 * ─── WHY IT REUSES 12 AND DOES NOT MINT A NEW TYPE ─────────────────────────────────────────────
 * Founder G1, 2026-07-18 — the collapsed-variant pattern: the SAME act on another surface keeps the
 * same type and says where it happened. A `PortalLoggedIn` would be a second name for one business
 * fact and would force every existing counter to learn about it. `extras {"surface":"portal"}` is
 * the one place this is allowed to carry a payload: it is not domain data (EXTRAS-1 is about
 * duplicating what the event owns), it is the variant discriminator the collapsed type needs.
 *
 * ─── SHAPE ─────────────────────────────────────────────────────────────────────────────────────
 * Refine's GraphQL dataProvider, not Apollo — the portal has no `ApolloProvider` at its root, and
 * a generated hook here throws "Could not find \"client\" in the context". `custom` speaks
 * `meta: {operation, fields, variables}`, the shape every other portal call uses.
 *
 * Telemetry must never be able to fail a sign-in, so every failure is swallowed after being
 * reported. A person who cannot see their clients because a counter POST 500'd would be a far
 * worse outcome than a missing row.
 */
export async function appendPortalUsageEvent(
  type: UsageEventTypes,
  targetId: string,
  extras?: Record<string, string>,
): Promise<void> {
  try {
    await gqlDataProvider.custom({
      url: '',
      method: 'post',
      meta: {
        operation: 'createUsageEvents',
        fields: ['ok'],
        variables: {
          events: {
            name: 'events',
            type: '[UsageEventInput!]',
            value: [
              {
                _to: targetId,
                createdAt: formatISO(new Date()),
                type,
                extras: extras ? JSON.stringify(extras) : undefined,
              },
            ],
          },
        },
      },
    });
  } catch (error) {
    Sentry.captureException(error, { tags: { usage: 'portal' } });
  }
}

/**
 * A successful portal sign-in — the one emit M7 adds here.
 *
 * Called from `authProvider`'s `onLogin`, which is the single point BOTH sign-in paths (email and
 * Google) pass through, and which has just installed the bearer token on the GraphQL client. The
 * order matters: the mutation is authenticated, so emitting before the header is set would post as
 * an anonymous caller.
 */
export const appendPortalSignInUsage = (): Promise<void> =>
  appendPortalUsageEvent(UsageEventTypes.LoggedIn, FEATURE_FLAGS_IDS.login, {
    surface: 'portal',
  });

/**
 * E30 · `PORTAL_UPDATE_PROMPT_SHOWN` — ONCE PER TAB SESSION.
 *
 * A module-scope flag, not storage (registry `once-per-tab-session`, ruled as recommended): a
 * reloaded tab and a second tab each record their own row, and a dismissed-then-reshown card does
 * not. That counts PEOPLE WHO SAW THE OFFER, not offers made, which is the question a deploy
 * actually raises — "how many of my therapists have been told about this?". The alternative, once
 * per DEPLOY per person, needs the build id in storage and answers a question nobody has asked.
 *
 * The focus is the BUILD BEING LEFT — `portalBuild/<baked id>`, which `focusOf()` splits back into
 * `focusType: portalBuild` / `focus: <build id>`. `FEATURE_FLAGS_IDS` is deliberately NOT extended:
 * the build id is itself the domain noun (registry `A-usage-ordinal`).
 *
 * ─── THE FLAG IS SET ONLY WHEN A ROW CAN ACTUALLY LAND ─────────────────────────────────────────
 * `createUsageEvents` is `isAuthenticated`, and the check runs for the whole tab — including the
 * seconds before Firebase has restored the session, and including a person sitting on /login while
 * a deploy lands. Burning the once-per-tab flag on a post that authenticates as nobody would lose
 * the row for that whole tab session, so a signed-out emit is skipped WITHOUT setting the flag and
 * the next check (foreground, or the 15-minute tick) records it.
 */
let portalUpdatePromptRecorded = false;

/** Test seam: a fresh tab has not recorded anything yet. */
export const resetPortalUpdatePromptRecorded = (): void => {
  portalUpdatePromptRecorded = false;
};

export const appendPortalUpdatePromptShownUsage = async (
  bakedBuildId: string,
  isSignedIn: boolean,
): Promise<void> => {
  if (portalUpdatePromptRecorded || !isSignedIn) {
    return;
  }
  portalUpdatePromptRecorded = true;
  await appendPortalUsageEvent(
    UsageEventTypes.PortalUpdatePromptShown,
    portalBuildFocusTarget(bakedBuildId),
    { surface: 'portal' },
  );
};
